import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Calendar, CheckCircle2, Link2, Mail, Unlink } from "lucide-react";
import { Integrations } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { fmtDay } from "@/lib/format";
import type { IntegrationProviderStatus } from "@/api/types";
import { confirmDialog, toast } from "@/lib/feedback";

interface IntegrationsCardProps {
  provider: IntegrationProviderStatus;
}

// Map a raw provider key to a human-readable display name.
function providerName(key: string): string {
  if (key === "google") return "Google (Gmail & Calendar)";
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// Known OAuth scope substrings → short, readable labels (with an icon).
const SCOPE_LABELS: { match: string; label: string; Icon: typeof Mail }[] = [
  { match: "gmail.readonly", label: "Gmail (read-only)", Icon: Mail },
  { match: "gmail.send", label: "Gmail (send)", Icon: Mail },
  { match: "gmail", label: "Gmail", Icon: Mail },
  { match: "calendar.readonly", label: "Calendar (read-only)", Icon: Calendar },
  { match: "calendar.events", label: "Calendar events", Icon: Calendar },
  { match: "calendar", label: "Calendar", Icon: Calendar },
];

function readableScopes(scopes: string | null) {
  if (!scopes) return [];
  const seen = new Set<string>();
  const out: { label: string; Icon: typeof Mail }[] = [];
  for (const raw of scopes.split(/\s+/).filter(Boolean)) {
    const known = SCOPE_LABELS.find((s) => raw.includes(s.match));
    const label = known ? known.label : raw.replace(/^https?:\/\//, "");
    if (seen.has(label)) continue;
    seen.add(label);
    out.push({ label, Icon: known?.Icon ?? Link2 });
  }
  return out;
}

export default function IntegrationsCard({ provider }: IntegrationsCardProps) {
  const qc = useQueryClient();
  const name = providerName(provider.provider);

  const connectMutation = useMutation({
    mutationFn: () => Integrations.connect(provider.provider),
    onSuccess: ({ authorization_url }) => {
      // Top-level redirect to Google consent — keeps the same browser so the
      // HttpOnly state cookie set by the connect POST is sent back.
      window.location.assign(authorization_url);
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => Integrations.disconnect(provider.provider),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integrations"] }),
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const connected = provider.status === "connected";
  const errored = provider.status === "error";

  return (
    <div className="panel-tight p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-md font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
            {name}
            {connected && (
              <span className="chip bg-accent-azure/15 text-accent-azure">
                <CheckCircle2 className="w-3 h-3" /> Connected
              </span>
            )}
            {errored && (
              <span className="chip bg-accent-pink/15 text-accent-pink">
                <AlertCircle className="w-3 h-3" /> Error
              </span>
            )}
            {!connected && !errored && <span className="text-xs text-mut-500">Not connected</span>}
          </div>

          {connected && provider.account_email && (
            <div className="text-sm text-mut-300 mt-1 truncate">{provider.account_email}</div>
          )}

          {connected && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {readableScopes(provider.scopes).map(({ label, Icon }) => (
                <span key={label} className="chip bg-fg/[0.05] text-mut-300 text-2xs">
                  <Icon className="w-3 h-3" /> {label}
                </span>
              ))}
            </div>
          )}

          {connected && provider.connected_at && (
            <div className="text-xs text-mut-500 mt-2">
              Connected {fmtDay(provider.connected_at)}
            </div>
          )}

          {errored && (
            <div className="text-sm text-accent-pink mt-1">
              Connection error — reconnect to restore access.
            </div>
          )}
        </div>

        <div className="shrink-0">
          {connected ? (
            <button
              className="btn btn-ghost text-accent-pink"
              disabled={disconnectMutation.isPending}
              onClick={async () => {
                if (
                  await confirmDialog({
                    title: `Disconnect ${name}?`,
                    body: "LeadZeppelin stops syncing your email and calendar until you connect again.",
                    confirmLabel: "Disconnect",
                    danger: true,
                  })
                )
                  disconnectMutation.mutate();
              }}
            >
              <Unlink className="w-4 h-4" /> Disconnect
            </button>
          ) : (
            <button
              className="btn btn-primary"
              disabled={connectMutation.isPending}
              onClick={() => connectMutation.mutate()}
            >
              <Link2 className="w-4 h-4" /> {errored ? "Reconnect" : "Connect"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
